import { useState, useEffect, useMemo } from 'react'
import { Mail, Users, Send, Plus, Trash2, Search, Eye, Edit3, X, UserPlus, FileText } from 'lucide-react'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import './EmailMarketingHub.css'

const AUDIENCES = [
  { value: 'all', label: 'Todos' },
  { value: 'cognito', label: 'Solo usuarios registrados' },
  { value: 'external', label: 'Solo suscriptores externos' },
]

export default function EmailMarketingHub() {
  const { user } = useAuth()
  const [tab, setTab] = useState('subscribers') // subscribers | campaigns | compose
  const [loading, setLoading] = useState(true)

  // ── Subscribers state ──
  const [subscribers, setSubscribers] = useState([])
  const [subSearch, setSubSearch] = useState('')
  const [subSourceFilter, setSubSourceFilter] = useState('all')
  const [showAddSub, setShowAddSub] = useState(false)
  const [newSubForm, setNewSubForm] = useState({ email: '', name: '' })

  // ── Campaigns state ──
  const [campaigns, setCampaigns] = useState([])
  const [editingCampaign, setEditingCampaign] = useState(null)
  const [showPreview, setShowPreview] = useState(false)

  // ── Compose state ──
  const [composeForm, setComposeForm] = useState({
    subject: '', htmlBody: '', textBody: '', audience: 'all'
  })

  const [processing, setProcessing] = useState(false)
  const [message, setMessage] = useState(null)

  // ── Load data ──
  const loadSubscribers = async () => {
    try {
      const data = await adminApi.get('/admin/newsletter/subscribers')
      setSubscribers(data.subscribers || [])
    } catch (e) {
      console.error('Error loading subscribers:', e)
    }
  }

  const loadCampaigns = async () => {
    try {
      const data = await adminApi.get('/admin/newsletter/campaigns')
      setCampaigns(data.campaigns || [])
    } catch (e) {
      console.error('Error loading campaigns:', e)
    }
  }

  useEffect(() => {
    Promise.all([loadSubscribers(), loadCampaigns()]).finally(() => setLoading(false))
  }, [])

  // ── Filtered subscribers ──
  const filteredSubs = useMemo(() => {
    let result = subscribers
    if (subSourceFilter !== 'all') {
      result = result.filter(s => s.source === subSourceFilter)
    }
    if (subSearch) {
      const q = subSearch.toLowerCase()
      result = result.filter(s =>
        s.email?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q)
      )
    }
    return result
  }, [subscribers, subSearch, subSourceFilter])

  // ── KPIs ──
  const totalSubs = subscribers.length
  const cognitoSubs = subscribers.filter(s => s.source === 'cognito').length
  const externalSubs = subscribers.filter(s => s.source === 'external').length
  const totalCampaigns = campaigns.length
  const sentCampaigns = campaigns.filter(c => c.status === 'sent').length

  // ── Handlers ──
  const handleAddSubscriber = async () => {
    if (!newSubForm.email) return
    setProcessing(true)
    try {
      await adminApi.post('/admin/newsletter/subscribers', newSubForm)
      setNewSubForm({ email: '', name: '' })
      setShowAddSub(false)
      await loadSubscribers()
      flash('Suscriptor agregado')
    } catch (e) {
      flash('Error: ' + (e.message || 'No se pudo agregar'), true)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteSubscriber = async (id) => {
    if (!window.confirm('¿Eliminar este suscriptor?')) return
    setProcessing(true)
    try {
      await adminApi.delete(`/admin/newsletter/subscribers/${encodeURIComponent(id)}`)
      await loadSubscribers()
      flash('Suscriptor eliminado')
    } catch (e) {
      flash('Error: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const handleSaveCampaign = async () => {
    if (!composeForm.subject) return flash('El asunto es requerido', true)
    if (!composeForm.htmlBody && !composeForm.textBody) return flash('El contenido es requerido', true)
    setProcessing(true)
    try {
      if (editingCampaign) {
        await adminApi.patch(`/admin/newsletter/campaigns/${editingCampaign}`, composeForm)
        flash('Campaña actualizada')
      } else {
        await adminApi.post('/admin/newsletter/campaigns', composeForm)
        flash('Campaña creada')
      }
      setComposeForm({ subject: '', htmlBody: '', textBody: '', audience: 'all' })
      setEditingCampaign(null)
      setTab('campaigns')
      await loadCampaigns()
    } catch (e) {
      flash('Error: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const handleEditCampaign = async (id) => {
    try {
      const data = await adminApi.get(`/admin/newsletter/campaigns/${id}`)
      setComposeForm({
        subject: data.subject || '',
        htmlBody: data.htmlBody || '',
        textBody: data.textBody || '',
        audience: data.audience || 'all'
      })
      setEditingCampaign(id)
      setTab('compose')
    } catch (e) {
      flash('Error cargando campaña: ' + e.message, true)
    }
  }

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña?')) return
    setProcessing(true)
    try {
      await adminApi.delete(`/admin/newsletter/campaigns/${id}`)
      await loadCampaigns()
      flash('Campaña eliminada')
    } catch (e) {
      flash('Error: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const handleSendCampaign = async (id) => {
    const campaign = campaigns.find(c => c.$id === id || c.SK === id)
    const subjectText = campaign?.subject || id
    if (!window.confirm(`¿Enviar la campaña "${subjectText}" a todos los suscriptores? Esta acción no se puede deshacer.`)) return
    setProcessing(true)
    try {
      const result = await adminApi.post(`/admin/newsletter/campaigns/${id}/send`)
      flash(`Enviado: ${result.sentCount}/${result.total} emails${result.errorCount ? ` (${result.errorCount} errores)` : ''}`)
      await loadCampaigns()
    } catch (e) {
      flash('Error enviando: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const handleTestEmail = async () => {
    if (!composeForm.subject) return flash('El asunto es requerido', true)
    setProcessing(true)
    try {
      await adminApi.post('/admin/newsletter/test', {
        subject: composeForm.subject,
        htmlBody: composeForm.htmlBody,
        textBody: composeForm.textBody,
        testEmail: user?.email
      })
      flash(`Email de prueba enviado a ${user?.email}`)
    } catch (e) {
      flash('Error enviando test: ' + e.message, true)
    } finally {
      setProcessing(false)
    }
  }

  const flash = (msg, isError = false) => {
    setMessage({ text: msg, isError })
    setTimeout(() => setMessage(null), 5000)
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

  if (loading) return <div className="em-loading">Cargando email marketing...</div>

  return (
    <div className="em-container">
      <div className="em-header">
        <div>
          <h1 className="em-title"><Mail size={28} /> Email Marketing</h1>
          <p className="em-subtitle">Gestión de suscriptores y campañas de email</p>
        </div>
      </div>

      {/* Message banner */}
      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          background: message.isError ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
          color: message.isError ? '#f87171' : '#34d399',
          fontSize: '0.85rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}><X size={16} /></button>
        </div>
      )}

      {/* KPIs */}
      <div className="em-kpi-row">
        <div className="em-kpi">
          <span className="em-kpi-value">{totalSubs}</span>
          <span className="em-kpi-label">Total Suscriptores</span>
        </div>
        <div className="em-kpi">
          <span className="em-kpi-value" style={{ color: '#818cf8' }}>{cognitoSubs}</span>
          <span className="em-kpi-label">Usuarios Cognito</span>
        </div>
        <div className="em-kpi">
          <span className="em-kpi-value" style={{ color: '#34d399' }}>{externalSubs}</span>
          <span className="em-kpi-label">Suscriptores Externos</span>
        </div>
        <div className="em-kpi">
          <span className="em-kpi-value" style={{ color: '#fbbf24' }}>{sentCampaigns}/{totalCampaigns}</span>
          <span className="em-kpi-label">Campañas Enviadas</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="em-tabs">
        <button className={`em-tab ${tab === 'subscribers' ? 'active' : ''}`} onClick={() => setTab('subscribers')}>
          <Users size={16} /> Suscriptores <span className="tab-count">{totalSubs}</span>
        </button>
        <button className={`em-tab ${tab === 'campaigns' ? 'active' : ''}`} onClick={() => setTab('campaigns')}>
          <FileText size={16} /> Campañas <span className="tab-count">{totalCampaigns}</span>
        </button>
        <button className={`em-tab ${tab === 'compose' ? 'active' : ''}`} onClick={() => { setTab('compose'); setEditingCampaign(null); setComposeForm({ subject: '', htmlBody: '', textBody: '', audience: 'all' }) }}>
          <Edit3 size={16} /> Componer
        </button>
      </div>

      {/* ═══ SUBSCRIBERS TAB ═══ */}
      {tab === 'subscribers' && (
        <>
          <div className="em-toolbar">
            <div className="em-search-wrap">
              <Search size={16} />
              <input className="em-search" placeholder="Buscar por email o nombre..." value={subSearch} onChange={e => setSubSearch(e.target.value)} />
            </div>
            <select className="em-filter-select" value={subSourceFilter} onChange={e => setSubSourceFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="cognito">Usuarios registrados</option>
              <option value="external">Suscriptores externos</option>
            </select>
            <button className="em-btn em-btn-primary" onClick={() => setShowAddSub(true)}>
              <UserPlus size={16} /> Agregar
            </button>
          </div>

          <div className="em-table-wrap">
            <table className="em-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Origen</th>
                  <th>Fecha</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubs.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No se encontraron suscriptores</td></tr>
                ) : filteredSubs.map(sub => (
                  <tr key={sub.id}>
                    <td>{sub.email}</td>
                    <td>{sub.name || '—'}</td>
                    <td>
                      <span className={`em-badge ${sub.source === 'cognito' ? 'em-badge-cognito' : sub.source === 'manual' ? 'em-badge-manual' : 'em-badge-external'}`}>
                        {sub.source === 'cognito' ? 'Usuario' : sub.source === 'manual' ? 'Manual' : 'Web'}
                      </span>
                    </td>
                    <td>{formatDate(sub.subscribedAt)}</td>
                    <td>
                      {sub.source !== 'cognito' && (
                        <button className="em-btn em-btn-danger" style={{ padding: '0.3rem 0.5rem' }} onClick={() => handleDeleteSubscriber(sub.id)} disabled={processing}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add subscriber modal */}
          {showAddSub && (
            <div className="em-modal-overlay" onClick={() => setShowAddSub(false)}>
              <div className="em-modal" onClick={e => e.stopPropagation()}>
                <h3><UserPlus size={18} /> Agregar Suscriptor</h3>
                <div className="em-form-group">
                  <label>Email *</label>
                  <input type="email" value={newSubForm.email} onChange={e => setNewSubForm(f => ({ ...f, email: e.target.value }))} placeholder="email@ejemplo.com" />
                </div>
                <div className="em-form-group">
                  <label>Nombre</label>
                  <input value={newSubForm.name} onChange={e => setNewSubForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre completo" />
                </div>
                <div className="em-modal-actions">
                  <button className="em-btn em-btn-secondary" onClick={() => setShowAddSub(false)}>Cancelar</button>
                  <button className="em-btn em-btn-primary" onClick={handleAddSubscriber} disabled={processing || !newSubForm.email}>
                    {processing ? 'Guardando...' : 'Agregar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ CAMPAIGNS TAB ═══ */}
      {tab === 'campaigns' && (
        <>
          <div className="em-toolbar">
            <button className="em-btn em-btn-primary" onClick={() => { setTab('compose'); setEditingCampaign(null); setComposeForm({ subject: '', htmlBody: '', textBody: '', audience: 'all' }) }}>
              <Plus size={16} /> Nueva Campaña
            </button>
          </div>

          {campaigns.length === 0 ? (
            <div className="em-empty">
              <Mail size={48} />
              <p>No hay campañas creadas aún. Creá una nueva para empezar.</p>
            </div>
          ) : (
            <div className="em-campaigns">
              {campaigns.map(camp => (
                <div key={camp.$id || camp.SK} className="em-campaign-card">
                  <div className="em-campaign-header">
                    <div>
                      <div className="em-campaign-subject">{camp.subject}</div>
                      <div className="em-campaign-meta">
                        <span><span className={`em-badge ${camp.status === 'sent' ? 'em-status-sent' : 'em-status-draft'}`}>{camp.status === 'sent' ? 'Enviada' : 'Borrador'}</span></span>
                        <span><Users size={14} /> {AUDIENCES.find(a => a.value === camp.audience)?.label || camp.audience}</span>
                        {camp.sentAt && <span><Send size={14} /> Enviada: {formatDate(camp.sentAt)}</span>}
                        {camp.sentCount > 0 && <span>{camp.sentCount}/{camp.totalRecipients || '?'} enviados</span>}
                        {camp.errorCount > 0 && <span style={{ color: '#f87171' }}>{camp.errorCount} errores</span>}
                        <span>Creada: {formatDate(camp.$createdAt || camp.createdAt)}</span>
                        {camp.createdBy && <span>por {camp.createdBy}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="em-campaign-actions">
                    {camp.status !== 'sent' && (
                      <>
                        <button className="em-btn em-btn-secondary" onClick={() => handleEditCampaign(camp.$id || camp.SK)} disabled={processing}>
                          <Edit3 size={14} /> Editar
                        </button>
                        <button className="em-btn em-btn-success" onClick={() => handleSendCampaign(camp.$id || camp.SK)} disabled={processing}>
                          <Send size={14} /> Enviar
                        </button>
                      </>
                    )}
                    <button className="em-btn em-btn-danger" onClick={() => handleDeleteCampaign(camp.$id || camp.SK)} disabled={processing}>
                      <Trash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ COMPOSE TAB ═══ */}
      {tab === 'compose' && (
        <div className="em-compose">
          <h3 style={{ color: '#f1f5f9', marginBottom: '1.25rem', fontSize: '1.1rem' }}>
            {editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}
          </h3>

          <div className="em-form-group">
            <label>Asunto *</label>
            <input value={composeForm.subject} onChange={e => setComposeForm(f => ({ ...f, subject: e.target.value }))} placeholder="Asunto del email..." />
          </div>

          <div className="em-form-group">
            <label>Audiencia</label>
            <select value={composeForm.audience} onChange={e => setComposeForm(f => ({ ...f, audience: e.target.value }))}>
              {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>

          <div className="em-form-group">
            <label>Contenido HTML</label>
            <textarea value={composeForm.htmlBody} onChange={e => setComposeForm(f => ({ ...f, htmlBody: e.target.value }))} placeholder="<h1>Hola!</h1><p>Contenido del email en HTML...</p>" />
          </div>

          <div className="em-form-group">
            <label>Texto plano (fallback)</label>
            <textarea style={{ minHeight: '100px' }} value={composeForm.textBody} onChange={e => setComposeForm(f => ({ ...f, textBody: e.target.value }))} placeholder="Versión texto plano del email..." />
          </div>

          {/* Preview */}
          {showPreview && composeForm.htmlBody && (
            <div className="em-form-group">
              <label>Vista previa</label>
              <div className="em-preview" dangerouslySetInnerHTML={{ __html: composeForm.htmlBody }} />
            </div>
          )}

          <div className="em-compose-actions">
            <button className="em-btn em-btn-secondary" onClick={() => setShowPreview(!showPreview)} disabled={!composeForm.htmlBody}>
              <Eye size={16} /> {showPreview ? 'Ocultar Preview' : 'Preview'}
            </button>
            <button className="em-btn em-btn-secondary" onClick={handleTestEmail} disabled={processing || !composeForm.subject}>
              <Send size={16} /> {processing ? 'Enviando...' : 'Enviar Test'}
            </button>
            <button className="em-btn em-btn-primary" onClick={handleSaveCampaign} disabled={processing || !composeForm.subject}>
              {processing ? 'Guardando...' : editingCampaign ? 'Actualizar Campaña' : 'Guardar Borrador'}
            </button>
            {editingCampaign && (
              <button className="em-btn em-btn-secondary" onClick={() => { setEditingCampaign(null); setComposeForm({ subject: '', htmlBody: '', textBody: '', audience: 'all' }) }}>
                <X size={16} /> Cancelar Edición
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
